/**
 * MachineReceiptsTab — unified registry of every receipt for a machine
 * (fuel stops, daybook expenses, maintenance receipts) sorted by date / KM,
 * with month-filter and one-click branded PDF download for any month or all-time.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Receipt, Download, Fuel, Wrench, FileText, Calendar } from "lucide-react";
import { Button, Card, Badge, Spinner, Select } from "../shared/ui";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kindIcon = (k) => {
  if (k === "fuel") return <Fuel className="w-4 h-4 text-orange-500" />;
  if (k === "maintenance") return <Wrench className="w-4 h-4 text-blue-600" />;
  return <Receipt className="w-4 h-4 text-green-600" />;
};

const monthOptions = () => {
  const out = [{ value: "", label: "All Time" }];
  const today = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({ value: v, label: d.toLocaleDateString("en-AU", { month: "long", year: "numeric" }) });
  }
  return out;
};

const MachineReceiptsTab = ({ machineId }) => {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/machines/${machineId}/receipts-registry`, { params: month ? { month } : {} });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load receipts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (machineId) load(); /* eslint-disable-next-line */ }, [machineId, month]);

  const downloadPdf = () => {
    const url = `${API}/machines/${machineId}/receipts-registry/pdf${month ? `?month=${month}` : ""}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="machine-receipts-tab">
      {/* Action bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            options={monthOptions()}
            testId="receipts-month"
          />
          <Badge>{data?.count ?? 0} receipts</Badge>
          {data?.total_amount > 0 && <Badge variant="success">${data.total_amount.toLocaleString()}</Badge>}
        </div>
        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="receipts-pdf-btn">
          <Download className="w-4 h-4 mr-1" /> Download PDF
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !data || data.items.length === 0 ? (
        <p className="text-center text-slate-500 py-8">No receipts logged for this {month ? "month" : "machine yet"}.</p>
      ) : (
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-left px-3 py-2">Job / Client</th>
                <th className="text-right px-3 py-2">Hrs</th>
                <th className="text-right px-3 py-2">KMs</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`receipt-row-${r.id}`}>
                  <td className="px-3 py-2">{r.date || "—"}</td>
                  <td className="px-3 py-2"><span className="inline-flex items-center gap-1">{kindIcon(r.kind)} {r.kind}</span></td>
                  <td className="px-3 py-2 max-w-[240px] truncate">{r.title}</td>
                  <td className="px-3 py-2 max-w-[200px] truncate text-slate-600">{r.job_title || r.client_name || "—"}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{r.ending_hours ?? r.starting_hours ?? "—"}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{r.ending_kms ?? r.starting_kms ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-bold">{r.amount ? `$${Number(r.amount).toLocaleString()}` : "—"}</td>
                  <td className="px-3 py-2">
                    {r.file_url && (
                      <a href={r.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">view</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default MachineReceiptsTab;
